import React, { useState, useEffect, useRef, useCallback } from 'react'
import ExternalIcon from '~/components/external_icon'

interface SearchItem {
  title: string
  path: string
  group: string
  type: 'guide' | 'app' | 'vertex'
  deprecated?: boolean
}

let dataPromise: Promise<SearchItem[]> | null = null

function fetchData(): Promise<SearchItem[]> {
  if (!dataPromise) {
    dataPromise = fetch('/docs/search.json')
      .then((r) => r.json())
      .catch(() => {
        dataPromise = null
        return []
      })
  }
  return dataPromise
}

interface Group {
  group: string
  items: SearchItem[]
  start: number
}

function groupItems(items: SearchItem[]): Group[] {
  const buckets = new Map<string, SearchItem[]>()
  items.forEach((item) => {
    const bucket = buckets.get(item.group)
    bucket ? bucket.push(item) : buckets.set(item.group, [item])
  })
  let start = 0
  return Array.from(buckets, ([group, items]) => {
    const entry = { group, items, start }
    start += items.length
    return entry
  })
}

function iconForType(type: SearchItem['type']) {
  switch (type) {
    case 'guide':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      )
    case 'app':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
        </svg>
      )
    case 'vertex':
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
      )
    default:
      return null
  }
}

function Modal({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchItem[]>([])
  const [activeIndex, setActiveIndex] = useState(-1)
  const hasResults = results.length > 0
  const inputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    fetchData()
    inputRef.current?.focus()
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const runSearch = useCallback((q: string) => {
    const trimmed = q.trim().toLowerCase()
    const normalized = trimmed.replace(/\s+/g, '')

    fetchData().then((data) => {
      const filtered = data.filter((item) => {
        const title = item.title.toLowerCase()
        return (
          title.includes(trimmed) ||
          title.replace(/\s+/g, '').includes(normalized)
        )
      })
      setResults(filtered.slice(0, 50))
    })
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setActiveIndex(-1)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (value.trim()) {
        runSearch(value)
      } else {
        setResults([])
      }
    }, 150)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter')
      return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = Math.min(activeIndex + 1, results.length - 1)
      setActiveIndex(next)
      itemRefs.current[next]?.scrollIntoView({ block: 'nearest' })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = Math.max(activeIndex - 1, -1)
      setActiveIndex(prev)
      if (prev >= 0)
        itemRefs.current[prev]?.scrollIntoView({ block: 'nearest' })
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      itemRefs.current[activeIndex]?.click()
    }
  }

  itemRefs.current = []

  return (
    <div
      className="docs-search-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="docs-search-dialog"
        role="dialog"
        aria-label="Search documentation"
      >
        <div
          className={`docs-search-input-row${
            hasResults ? ' docs-search-input-row--has-results' : ''
          }`}
        >
          <span className="docs-search-icon">
            <ExternalIcon symbol="general#search" size={16} />
          </span>
          <input
            ref={inputRef}
            className="docs-search-input"
            type="text"
            placeholder="Search documentation..."
            autoComplete="off"
            spellCheck={false}
            value={query}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
          />
          <span className="docs-search-esc">ESC</span>
        </div>

        <div
          className={`docs-search-results${
            hasResults ? ' docs-search-results--has-results' : ''
          }`}
        >
          {query.trim() && !hasResults && (
            <div className="docs-search-empty">No results found</div>
          )}
          {hasResults &&
            groupItems(results).map(({ group, items, start }) => (
              <div key={group} className="docs-search-group">
                <div className="docs-search-group-label">{group}</div>
                {items.map((item, i) => {
                  const idx = start + i
                  return (
                    <a
                      key={item.path}
                      ref={(el) => {
                        itemRefs.current[idx] = el
                      }}
                      className={`docs-search-item${
                        idx === activeIndex ? ' docs-search-item--active' : ''
                      }`}
                      href={item.path}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <span className="docs-search-item-icon">
                        {iconForType(item.type)}
                      </span>
                      <span
                        className={`docs-search-item-title${
                          item.deprecated
                            ? ' docs-search-item-title--deprecated'
                            : ''
                        }`}
                      >
                        {item.title}
                      </span>
                    </a>
                  )
                })}
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default function SearchModal() {
  const [open, setOpen] = useState(false)

  const openSearch = useCallback(() => setOpen(true), [])
  const closeSearch = useCallback(() => setOpen(false), [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        openSearch()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openSearch])

  return (
    <>
      <button
        type="button"
        className="tds-btn tds-btn--outline-neutral search-btn"
        aria-label="Search documentation"
        onClick={openSearch}
      >
        <ExternalIcon symbol="general#search" size={14} />
        <span className="search-btn--label">&#8984;K</span>
      </button>
      {open && <Modal onClose={closeSearch} />}
    </>
  )
}
